import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../../components/ui/Button';
import { Dropdown } from 'primereact/dropdown';
import { SelectButton } from 'primereact/selectbutton';

import Layout from '../../Layout/Layout';
import WallTabs from './components/WallTabs';
import { useIsRestaurant } from '../../../hooks/useAccountType';
import PlaylistPreviewPlayer from './preview/PlaylistPreviewPlayer';
import { useGetPlaylists, useGetPlaylist } from '../../../queries/wallPlaylistLibrary';
import { useGetWallMediaLibrary } from '../../../queries/videoWall';
import { useGetThemes } from '../../../queries/signageTemplates';

/**
 * Smart Template Engine — Preview System (Phase 4): Full Wall Simulator.
 *
 * Standalone page that plays a whole playlist on a simulated physical wall in real
 * time — menus rotate sections, videos span the grid, items auto-transition. A
 * speed control fast-forwards through the playlist (sales demo + quick verification).
 * Reuses the shared PlaylistPreviewPlayer + timeline engine. Read-only — no sync contact.
 */
const SPEEDS = [{ label: '1×', value: 1 }, { label: '2×', value: 2 }, { label: '4×', value: 4 }, { label: '8×', value: 8 }];

export default function VideoWallSimulator() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isRestaurant = useIsRestaurant();

  const { data: playlists = [] } = useGetPlaylists();
  const [playlistId, setPlaylistId] = useState(null);
  const [speed, setSpeed] = useState(1);

  const { data: playlist } = useGetPlaylist(playlistId, !!playlistId);
  const { data: mediaRaw } = useGetWallMediaLibrary(true);
  const { data: themeData } = useGetThemes();
  const themes = themeData?.themes ?? [];
  const defaultZones = useMemo(() => themeData?.defaultZones ?? {}, [themeData]);

  const mediaMap = useMemo(() => {
    const arr = mediaRaw?.data ?? mediaRaw ?? [];
    const m = {}; (Array.isArray(arr) ? arr : []).forEach((x) => { m[x.id] = x; }); return m;
  }, [mediaRaw]);
  const resolvePhoto = useMemo(() => (id) => mediaMap[id]?.path || mediaMap[id]?.thumb || null, [mediaMap]);

  const items = useMemo(() => playlist?.items ?? [], [playlist]);

  return (
    <Layout>
      <WallTabs />
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-4">
        <div>
          {!isRestaurant && (
          <Button text icon="pi pi-arrow-left" label={t('video_wall.library.back_to_walls')}
            onClick={() => navigate('/apps/video-wall/walls')} className="mb-2 p-0" />
          )}
          <h2 className="mb-1" style={{ color: 'var(--pp-text-primary)', fontWeight: 700 }}>
            <i className="pi pi-desktop me-2" style={{ color: 'var(--pp-primary)' }} />
            {t('video_wall.simulator.title')}
          </h2>
          <p className="mb-0" style={{ color: 'var(--pp-text-secondary)' }}>{t('video_wall.simulator.subtitle')}</p>
        </div>
      </div>

      <div className="pp-card p-4 mb-4 d-flex flex-wrap align-items-end gap-3">
        <div style={{ minWidth: 260 }}>
          <label className="d-block mb-1" style={{ color: 'var(--pp-text-secondary)', fontSize: 13 }}>{t('video_wall.simulator.playlist')}</label>
          <Dropdown className="w-100" value={playlistId} onChange={(e) => setPlaylistId(e.value)} filter
            options={playlists.map((p) => ({ label: p.name, value: p.id }))}
            placeholder={t('video_wall.simulator.pick_playlist')} />
        </div>
        <div>
          <label className="d-block mb-1" style={{ color: 'var(--pp-text-secondary)', fontSize: 13 }}>{t('video_wall.simulator.speed')}</label>
          <SelectButton value={speed} onChange={(e) => e.value && setSpeed(e.value)} options={SPEEDS} />
        </div>
      </div>

      {!playlistId ? (
        <div className="pp-card p-5 text-center">
          <span className="pp-empty-icon mb-3" style={{ width: 56, height: 56, fontSize: '1.5rem' }}><i className="pi pi-desktop" /></span>
          <h5 style={{ color: 'var(--pp-text-primary)' }}>{t('video_wall.simulator.empty_title')}</h5>
          <p className="text-muted">{t('video_wall.simulator.empty_subtitle')}</p>
        </div>
      ) : (
        <div className="pp-card p-4 d-flex justify-content-center">
          <PlaylistPreviewPlayer key={`${playlistId}-${speed}`} items={items} mediaMap={mediaMap}
            themes={themes} defaultZones={defaultZones} resolvePhoto={resolvePhoto} speed={speed} maxWidth={680} />
        </div>
      )}
    </Layout>
  );
}
